import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClickAwayListener, Popper } from "@material-ui/core";
import "./PopperMenu.scss";

const PopperMenu = ({
  anchorElement = null,
  className = "popper-menu-container",
  placement = null,
  onRequestClose = null,
  menuItems = [],
  getItemKey = null,
}) => {
  const open = Boolean(anchorElement);
  const isAlignLeft = className.includes("align-left");
  const isOpenUp = className.includes("open-up");
  const resolvedPlacement =
    placement ||
    `${isOpenUp ? "top" : "bottom"}-${isAlignLeft ? "start" : "end"}`;
  const [hoveredItemKey, setHoveredItemKey] = useState(null);

  const visibleItems = useMemo(
    () => (menuItems || []).filter((item) => !item?.isHidden),
    [menuItems]
  );

  const resolveItemKey = (item, index) => {
    if (typeof getItemKey === "function") {
      return getItemKey(item, index);
    }

    return item?.id || item?.key || item?.text || item?.label || index;
  };

  const resolveText = (item) => item?.text || item?.label || "";

  const renderMenuItemContent = (item, index, isSelected) => {
    const label = resolveText(item);
    const resolvedIconClassName = item?.iconClassName || "";
    const resolvedTextClassName = item?.textClassName || "popper-menu-text";

    return (
      <>
        {item?.icon && (
          <img
            src={
              isSelected && item?.selectedIcon ? item.selectedIcon : item.icon
            }
            alt={item?.alt || label}
            className={resolvedIconClassName}
          />
        )}
        {item?.customContent || (
          <span className={resolvedTextClassName}>{label}</span>
        )}
      </>
    );
  };

  const renderMenuItems = () => {
    if (!visibleItems.length) {
      return null;
    }

    return visibleItems.map((item, index) => {
      const itemKey = resolveItemKey(item, index);
      const isSelected = hoveredItemKey === itemKey;
      const resolvedItemClassName = item?.itemClassName || "popper-menu-item";
      const resolvedSelectedItemClassName = item?.selectedItemClassName || "";
      const resolvedLinkClassName = item?.linkClassName || "popper-menu-link";
      const combinedItemClassName = [
        resolvedItemClassName,
        isSelected && resolvedSelectedItemClassName
          ? resolvedSelectedItemClassName
          : "",
      ]
        .filter(Boolean)
        .join(" ");

      const commonProps = {
        key: itemKey,
        className: combinedItemClassName,
        onClick: item?.onClick || null,
        onMouseEnter: () => setHoveredItemKey(itemKey),
        onMouseLeave: () => setHoveredItemKey(null),
      };

      if (item?.to) {
        return (
          <Link to={item.to} {...commonProps}>
            {renderMenuItemContent(item, index, isSelected)}
          </Link>
        );
      }

      if (item?.url || item?.isUrl) {
        return (
          <a
            href={item.url}
            className={[combinedItemClassName, resolvedLinkClassName]
              .filter(Boolean)
              .join(" ")}
            key={itemKey}
            onClick={item?.onClick || null}
            onMouseEnter={() => setHoveredItemKey(itemKey)}
            onMouseLeave={() => setHoveredItemKey(null)}
          >
            {renderMenuItemContent(item, index, isSelected)}
          </a>
        );
      }

      return (
        <div {...commonProps}>
          {renderMenuItemContent(item, index, isSelected)}
        </div>
      );
    });
  };

  if (!open) {
    return null;
  }

  return (
    <Popper
      anchorEl={anchorElement}
      open={open}
      placement={resolvedPlacement}
      className="popper-menu"
      modifiers={{
        offset: {
          enabled: true,
          offset: "0, 0",
        },
        preventOverflow: {
          enabled: true,
          boundariesElement: "viewport",
          padding: 8,
        },
      }}
    >
      <ClickAwayListener onClickAway={onRequestClose}>
        <div className={className}>{renderMenuItems()}</div>
      </ClickAwayListener>
    </Popper>
  );
};

export default PopperMenu;
